import React, { useState, useEffect, useCallback } from 'react';
import './WooCommerceIntegration.css';

// Modern SVG Icons
const Icons = {
  Products: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
      <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
  Customers: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Orders: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  All: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/>
      <rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/>
      <rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  Refresh: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10"/>
      <polyline points="1 20 1 14 7 14"/>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
    </svg>
  ),
  Check: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  Search: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  ),
  ChevronLeft: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  ),
  ChevronRight: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  )
};

/**
 * WooCommerce Integration Modal Component
 * Multi-step wizard for connecting and importing from WooCommerce
 */
const WooCommerceIntegration = ({ isOpen, onClose, onStatusChange, toast }) => {
  // Connection form state
  const [storeUrl, setStoreUrl] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [connecting, setConnecting] = useState(false);

  // Connection status
  const [status, setStatus] = useState({
    connected: false,
    storeName: null,
    storeUrl: null,
    storeVersion: null,
    currency: null,
    loading: true
  });

  // Store counts from WooCommerce
  const [storeCounts, setStoreCounts] = useState({
    products: 0,
    orders: 0,
    customers: 0,
    loading: false,
    error: null
  });

  // Product validation
  const [productValidation, setProductValidation] = useState(null);

  // Import state
  const [currentStep, setCurrentStep] = useState('connect'); // connect, import, select, progress, complete
  const [importType, setImportType] = useState('PRODUCTS');
  const [importMode, setImportMode] = useState('all'); // 'all' or 'selected'
  const [importOptions, setImportOptions] = useState({
    skipDuplicates: true,
    updateExisting: false
  });
  const [importJob, setImportJob] = useState(null);
  const [importProgress, setImportProgress] = useState(null);

  // Selective import state
  const [previewItems, setPreviewItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPagination, setPreviewPagination] = useState({ page: 1, totalPages: 1, totalItems: 0 });
  const [searchQuery, setSearchQuery] = useState('');

  // Import history
  const [importHistory, setImportHistory] = useState([]);

  // Fetch connection status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/merchant/woocommerce/status', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStatus({
          connected: data.connected || false,
          storeName: data.integration?.storeName || null,
          storeUrl: data.integration?.storeUrl || null,
          storeVersion: data.integration?.storeVersion || null,
          currency: data.integration?.currency || null,
          loading: false
        });

        if (data.connected) {
          setCurrentStep('import');
          fetchStoreCounts();
          fetchProductValidation();
          fetchImportHistory();
        }
      } else {
        setStatus(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error fetching WooCommerce status:', error);
      setStatus(prev => ({ ...prev, loading: false }));
    }
  }, []);

  // Fetch store counts with retry
  const fetchStoreCounts = async () => {
    setStoreCounts(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch('/api/merchant/woocommerce/counts', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStoreCounts({
          products: data.counts?.products || 0,
          orders: data.counts?.orders || 0,
          customers: data.counts?.customers || 0,
          loading: false,
          error: null
        });
      } else {
        throw new Error('Failed to fetch counts');
      }
    } catch (error) {
      console.error('Error fetching store counts:', error);
      setStoreCounts(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load store data. Click retry to try again.'
      }));
    }
  };

  // Fetch product validation (tier limits)
  const fetchProductValidation = async () => {
    try {
      const response = await fetch('/api/merchant/woocommerce/validate-products', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProductValidation(data.validation);
      }
    } catch (error) {
      console.error('Error fetching product validation:', error);
    }
  };

  // Fetch import history
  const fetchImportHistory = async () => {
    try {
      const response = await fetch('/api/merchant/woocommerce/import-history?limit=5', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setImportHistory(data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching import history:', error);
    }
  };

  // Fetch preview items for selective import
  const fetchPreviewItems = async (page = 1) => {
    setPreviewLoading(true);
    try {
      const type = importType.toLowerCase();
      const params = new URLSearchParams({
        page: page.toString(),
        perPage: '20',
        ...(searchQuery && { search: searchQuery })
      });

      const response = await fetch(`/api/merchant/woocommerce/preview/${type}?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewItems(data.items || []);
        setPreviewPagination({
          page: data.pagination.page,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems
        });
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error('Failed to load items for preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Connect WooCommerce store
  const handleConnect = async (e) => {
    e.preventDefault();
    setConnecting(true);

    try {
      const response = await fetch('/api/merchant/woocommerce/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          storeUrl,
          consumerKey,
          consumerSecret
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('WooCommerce store connected successfully!');
        setStatus({
          connected: true,
          storeName: data.integration?.storeName,
          storeUrl: data.integration?.storeUrl,
          storeVersion: data.integration?.storeVersion,
          currency: data.integration?.currency,
          loading: false
        });
        setCurrentStep('import');
        fetchStoreCounts();
        fetchProductValidation();
        onStatusChange?.();
      } else {
        toast.error(data.error || 'Failed to connect WooCommerce store');
      }
    } catch (error) {
      console.error('Error connecting WooCommerce:', error);
      toast.error('Failed to connect WooCommerce store');
    } finally {
      setConnecting(false);
    }
  };

  // Disconnect WooCommerce store
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your WooCommerce store?')) {
      return;
    }

    try {
      const response = await fetch('/api/merchant/woocommerce/disconnect', {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('WooCommerce store disconnected');
        setStatus({
          connected: false,
          storeName: null,
          storeUrl: null,
          storeVersion: null,
          currency: null,
          loading: false
        });
        setCurrentStep('connect');
        setStoreUrl('');
        setConsumerKey('');
        setConsumerSecret('');
        onStatusChange?.();
      }
    } catch (error) {
      console.error('Error disconnecting WooCommerce:', error);
      toast.error('Failed to disconnect WooCommerce store');
    }
  };

  // Handle import type change
  const handleImportTypeChange = (type) => {
    setImportType(type);
    setSelectedIds([]);
    setPreviewItems([]);
    if (type === 'ALL') {
      setImportMode('all');
    }
  };

  // Proceed to selection step
  const handleProceedToSelect = () => {
    if (importMode === 'selected' && importType !== 'ALL') {
      setSelectedIds([]);
      setCurrentStep('select');
      fetchPreviewItems(1);
    } else {
      handleStartImport();
    }
  };

  // Toggle item selection
  const toggleItemSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Select all items on current page
  const selectAllOnPage = () => {
    const pageIds = previewItems.map(item => item.id);
    const allSelected = pageIds.every(id => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...pageIds])]);
    }
  };

  // Start import
  const handleStartImport = async () => {
    try {
      const endpoint = importMode === 'selected' && selectedIds.length > 0
        ? '/api/merchant/woocommerce/import/selected'
        : '/api/merchant/woocommerce/import';

      const body = importMode === 'selected' && selectedIds.length > 0
        ? { importType, selectedIds, options: importOptions }
        : { importType, options: importOptions };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (data.success) {
        setImportJob(data.job);
        setCurrentStep('progress');
        pollImportProgress(data.job.id);
      } else {
        toast.error(data.error || 'Failed to start import');
      }
    } catch (error) {
      console.error('Error starting import:', error);
      toast.error('Failed to start import');
    }
  };

  // Poll import progress
  const pollImportProgress = async (jobId) => {
    try {
      const response = await fetch(`/api/merchant/woocommerce/import/${jobId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setImportProgress(data);

        if (data.status === 'RUNNING' || data.status === 'PENDING') {
          setTimeout(() => pollImportProgress(jobId), 2000);
        } else if (data.status === 'COMPLETED') {
          setCurrentStep('complete');
          toast.success('Import completed successfully!');
          fetchImportHistory();
        } else if (data.status === 'FAILED') {
          toast.error('Import failed. Check error log for details.');
        } else if (data.status === 'CANCELLED') {
          toast.info('Import was cancelled');
        }
      }
    } catch (error) {
      console.error('Error polling import progress:', error);
    }
  };

  // Cancel import
  const handleCancelImport = async () => {
    if (!importJob) return;

    try {
      const response = await fetch(`/api/merchant/woocommerce/import/${importJob.id}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.info('Import cancelled');
        setCurrentStep('import');
      }
    } catch (error) {
      console.error('Error cancelling import:', error);
    }
  };

  // Reset to import step
  const handleImportMore = () => {
    setImportJob(null);
    setImportProgress(null);
    setSelectedIds([]);
    setImportMode('all');
    setCurrentStep('import');
    fetchStoreCounts();
    fetchProductValidation();
  };

  // Back to import step from select
  const handleBackToImport = () => {
    setCurrentStep('import');
    setSelectedIds([]);
    setPreviewItems([]);
  };

  // Initial fetch
  useEffect(() => {
    if (isOpen) {
      fetchStatus();
    }
  }, [isOpen, fetchStatus]);

  // Search debounce
  useEffect(() => {
    if (currentStep === 'select') {
      const timer = setTimeout(() => {
        fetchPreviewItems(1);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchQuery]);

  if (!isOpen) return null;

  return (
    <div className="woo-modal-overlay" onClick={onClose}>
      <div className="woo-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="woo-modal-header">
          <div className="woo-modal-header-left">
            <div className="woo-modal-icon">
              <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
                <path d="M21.32 8.5c0-2.7-2.2-4.95-4.95-4.95S11.42 5.8 11.42 8.5c0 1.85 1.03 3.47 2.55 4.3l-.9 3.35 3.25-2.1c.2.02.4.03.6.03 2.7 0 4.95-2.25 4.95-4.95-.55.55-.32.37-.55.37zm-3.47-2.9c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2.75 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM2 17.5V4h18v1.55c-.63-.35-1.33-.55-2.05-.55C14.7 5 12 7.7 12 11c0 1.65.65 3.15 1.7 4.25L12.5 19l-3-1.9-5.5 3.4V17.5H2z"/>
              </svg>
            </div>
            <h2 className="woo-modal-title">WooCommerce Integration</h2>
          </div>
          <button className="woo-modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="woo-modal-body">
          {status.loading ? (
            <div className="woo-loading">
              <div className="woo-spinner"></div>
              <p>Loading...</p>
            </div>
          ) : currentStep === 'connect' ? (
            // CONNECT STEP
            <div className="woo-step woo-step-connect">
              <div className="woo-intro">
                <p>Connect your WooCommerce store to import products, orders, and customers to Revstar.</p>
              </div>

              <form onSubmit={handleConnect} className="woo-connect-form">
                <div className="woo-form-group">
                  <label>Store URL</label>
                  <input
                    type="url"
                    value={storeUrl}
                    onChange={(e) => setStoreUrl(e.target.value)}
                    placeholder="https://yourstore.com"
                    required
                  />
                  <span className="woo-form-hint">Your WooCommerce store URL</span>
                </div>

                <div className="woo-form-group">
                  <label>Consumer Key</label>
                  <input
                    type="text"
                    value={consumerKey}
                    onChange={(e) => setConsumerKey(e.target.value)}
                    placeholder="ck_xxxxxxxxxxxxx"
                    required
                  />
                </div>

                <div className="woo-form-group">
                  <label>Consumer Secret</label>
                  <input
                    type="password"
                    value={consumerSecret}
                    onChange={(e) => setConsumerSecret(e.target.value)}
                    placeholder="cs_xxxxxxxxxxxxx"
                    required
                  />
                </div>

                <div className="woo-help-box">
                  <h4>How to get API keys:</h4>
                  <ol>
                    <li>Go to your WordPress admin panel</li>
                    <li>Navigate to WooCommerce &gt; Settings &gt; Advanced &gt; REST API</li>
                    <li>Click "Add key" and set permissions to "Read"</li>
                    <li>Copy the Consumer Key and Consumer Secret</li>
                  </ol>
                </div>

                <button
                  type="submit"
                  className="woo-btn woo-btn-primary"
                  disabled={connecting}
                >
                  {connecting ? 'Connecting...' : 'Connect Store'}
                </button>
              </form>
            </div>
          ) : currentStep === 'import' ? (
            // IMPORT STEP
            <div className="woo-step woo-step-import">
              {/* Connection Status */}
              <div className="woo-connected-box">
                <div className="woo-connected-info">
                  <span className="woo-connected-dot"></span>
                  <div>
                    <strong>{status.storeName || 'WooCommerce Store'}</strong>
                    <p>{status.storeUrl}</p>
                  </div>
                </div>
                <button className="woo-btn woo-btn-outline woo-btn-sm" onClick={handleDisconnect}>
                  Disconnect
                </button>
              </div>

              {/* Store Counts */}
              <div className="woo-counts-section">
                <div className="woo-counts-header">
                  <span>Store Data</span>
                  <button
                    className="woo-refresh-btn"
                    onClick={fetchStoreCounts}
                    disabled={storeCounts.loading}
                    title="Refresh counts"
                  >
                    <Icons.Refresh />
                    {storeCounts.loading ? 'Loading...' : 'Refresh'}
                  </button>
                </div>

                {storeCounts.error ? (
                  <div className="woo-counts-error">
                    <p>{storeCounts.error}</p>
                    <button className="woo-btn woo-btn-outline woo-btn-sm" onClick={fetchStoreCounts}>
                      <Icons.Refresh /> Retry
                    </button>
                  </div>
                ) : (
                  <div className="woo-counts-grid">
                    <div className="woo-count-card">
                      <span className="woo-count-value">{storeCounts.loading ? '...' : storeCounts.products}</span>
                      <span className="woo-count-label">Products</span>
                    </div>
                    <div className="woo-count-card">
                      <span className="woo-count-value">{storeCounts.loading ? '...' : storeCounts.orders}</span>
                      <span className="woo-count-label">Orders</span>
                    </div>
                    <div className="woo-count-card">
                      <span className="woo-count-value">{storeCounts.loading ? '...' : storeCounts.customers}</span>
                      <span className="woo-count-label">Customers</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Product Limit Warning */}
              {productValidation && importType === 'PRODUCTS' && productValidation.willSkip > 0 && (
                <div className="woo-warning-box">
                  <strong>Product Limit Warning</strong>
                  <p>
                    You have {productValidation.availableSlots} available product slots.
                    Only the first {productValidation.willImport} products will be imported.
                    {productValidation.willSkip} products will be skipped.
                  </p>
                </div>
              )}

              {/* Import Type Selection */}
              <div className="woo-import-section">
                <h4>What would you like to import?</h4>
                <div className="woo-import-options">
                  <label className={`woo-import-option ${importType === 'PRODUCTS' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="importType"
                      value="PRODUCTS"
                      checked={importType === 'PRODUCTS'}
                      onChange={(e) => handleImportTypeChange(e.target.value)}
                    />
                    <div className="woo-import-option-content">
                      <span className="woo-import-option-icon"><Icons.Products /></span>
                      <span className="woo-import-option-label">Products</span>
                      <span className="woo-import-option-count">{storeCounts.products}</span>
                    </div>
                  </label>

                  <label className={`woo-import-option ${importType === 'CUSTOMERS' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="importType"
                      value="CUSTOMERS"
                      checked={importType === 'CUSTOMERS'}
                      onChange={(e) => handleImportTypeChange(e.target.value)}
                    />
                    <div className="woo-import-option-content">
                      <span className="woo-import-option-icon"><Icons.Customers /></span>
                      <span className="woo-import-option-label">Customers</span>
                      <span className="woo-import-option-count">{storeCounts.customers}</span>
                    </div>
                  </label>

                  <label className={`woo-import-option ${importType === 'ORDERS' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="importType"
                      value="ORDERS"
                      checked={importType === 'ORDERS'}
                      onChange={(e) => handleImportTypeChange(e.target.value)}
                    />
                    <div className="woo-import-option-content">
                      <span className="woo-import-option-icon"><Icons.Orders /></span>
                      <span className="woo-import-option-label">Orders</span>
                      <span className="woo-import-option-count">{storeCounts.orders}</span>
                    </div>
                  </label>

                  <label className={`woo-import-option ${importType === 'ALL' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="importType"
                      value="ALL"
                      checked={importType === 'ALL'}
                      onChange={(e) => handleImportTypeChange(e.target.value)}
                    />
                    <div className="woo-import-option-content">
                      <span className="woo-import-option-icon"><Icons.All /></span>
                      <span className="woo-import-option-label">All Data</span>
                      <span className="woo-import-option-count">
                        {storeCounts.products + storeCounts.customers + storeCounts.orders}
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Import Mode Selection */}
              {importType !== 'ALL' && (
                <div className="woo-import-mode-section">
                  <h4>Import Mode</h4>
                  <div className="woo-import-mode-options">
                    <label className={`woo-mode-option ${importMode === 'all' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="importMode"
                        value="all"
                        checked={importMode === 'all'}
                        onChange={(e) => setImportMode(e.target.value)}
                      />
                      <div className="woo-mode-content">
                        <strong>Import All</strong>
                        <span>Import all {importType.toLowerCase()} from your store</span>
                      </div>
                    </label>
                    <label className={`woo-mode-option ${importMode === 'selected' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="importMode"
                        value="selected"
                        checked={importMode === 'selected'}
                        onChange={(e) => setImportMode(e.target.value)}
                      />
                      <div className="woo-mode-content">
                        <strong>Select Items</strong>
                        <span>Choose specific {importType.toLowerCase()} to import</span>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Import Options */}
              <div className="woo-options-section">
                <h4>Import Options</h4>
                <label className="woo-checkbox">
                  <input
                    type="checkbox"
                    checked={importOptions.skipDuplicates}
                    onChange={(e) => setImportOptions(prev => ({ ...prev, skipDuplicates: e.target.checked }))}
                  />
                  <span>Skip duplicate records</span>
                </label>
              </div>

              <button
                className="woo-btn woo-btn-primary woo-btn-lg"
                onClick={handleProceedToSelect}
              >
                {importMode === 'selected' && importType !== 'ALL' ? 'Select Items' : 'Start Import'}
              </button>

              {/* Import History */}
              {importHistory.length > 0 && (
                <div className="woo-history-section">
                  <h4>Recent Imports</h4>
                  <div className="woo-history-list">
                    {importHistory.map(job => (
                      <div key={job.id} className="woo-history-item">
                        <span className={`woo-history-status woo-status-${job.status.toLowerCase()}`}>
                          {job.status}
                        </span>
                        <span className="woo-history-type">{job.importType}</span>
                        <span className="woo-history-stats">
                          {job.successCount} imported, {job.skippedCount} skipped
                        </span>
                        <span className="woo-history-date">
                          {new Date(job.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : currentStep === 'select' ? (
            // SELECT STEP
            <div className="woo-step woo-step-select">
              <div className="woo-select-header">
                <button className="woo-back-btn" onClick={handleBackToImport}>
                  <Icons.ChevronLeft /> Back
                </button>
                <h3>Select {importType.charAt(0) + importType.slice(1).toLowerCase()} to Import</h3>
              </div>

              {/* Search */}
              <div className="woo-search-box">
                <Icons.Search />
                <input
                  type="text"
                  placeholder={`Search ${importType.toLowerCase()}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Selection summary */}
              <div className="woo-selection-summary">
                <span>{selectedIds.length} selected</span>
                <button className="woo-link-btn" onClick={selectAllOnPage}>
                  {previewItems.every(item => selectedIds.includes(item.id)) ? 'Deselect all' : 'Select all on page'}
                </button>
              </div>

              {/* Items list */}
              {previewLoading ? (
                <div className="woo-loading">
                  <div className="woo-spinner"></div>
                  <p>Loading items...</p>
                </div>
              ) : (
                <div className="woo-items-list">
                  {previewItems.map(item => (
                    <div
                      key={item.id}
                      className={`woo-item-row ${selectedIds.includes(item.id) ? 'selected' : ''}`}
                      onClick={() => toggleItemSelection(item.id)}
                    >
                      <div className="woo-item-checkbox">
                        {selectedIds.includes(item.id) && <Icons.Check />}
                      </div>
                      {importType === 'PRODUCTS' ? (
                        <>
                          <div className="woo-item-image">
                            {item.image ? (
                              <img src={item.image} alt={item.name} />
                            ) : (
                              <div className="woo-item-placeholder"><Icons.Products /></div>
                            )}
                          </div>
                          <div className="woo-item-info">
                            <strong>{item.name}</strong>
                            <span>{item.sku ? `SKU: ${item.sku}` : 'No SKU'} | Stock: {item.stock}</span>
                          </div>
                          <div className="woo-item-price">{status.currency || '$'}{item.price}</div>
                        </>
                      ) : importType === 'CUSTOMERS' ? (
                        <>
                          <div className="woo-item-avatar">
                            {item.avatar ? (
                              <img src={item.avatar} alt="" />
                            ) : (
                              <div className="woo-item-placeholder"><Icons.Customers /></div>
                            )}
                          </div>
                          <div className="woo-item-info">
                            <strong>{item.firstName} {item.lastName}</strong>
                            <span>{item.email}</span>
                          </div>
                          <div className="woo-item-meta">{item.ordersCount} orders</div>
                        </>
                      ) : (
                        <>
                          <div className="woo-item-icon"><Icons.Orders /></div>
                          <div className="woo-item-info">
                            <strong>Order #{item.number}</strong>
                            <span>{item.customerName} | {item.itemCount} items</span>
                          </div>
                          <div className="woo-item-price">{item.currency}{item.total}</div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {previewPagination.totalPages > 1 && (
                <div className="woo-pagination">
                  <button
                    className="woo-page-btn"
                    disabled={previewPagination.page <= 1}
                    onClick={() => fetchPreviewItems(previewPagination.page - 1)}
                  >
                    <Icons.ChevronLeft /> Prev
                  </button>
                  <span>Page {previewPagination.page} of {previewPagination.totalPages}</span>
                  <button
                    className="woo-page-btn"
                    disabled={previewPagination.page >= previewPagination.totalPages}
                    onClick={() => fetchPreviewItems(previewPagination.page + 1)}
                  >
                    Next <Icons.ChevronRight />
                  </button>
                </div>
              )}

              {/* Import button */}
              <button
                className="woo-btn woo-btn-primary woo-btn-lg"
                onClick={handleStartImport}
                disabled={selectedIds.length === 0}
              >
                Import {selectedIds.length} Selected Items
              </button>
            </div>
          ) : currentStep === 'progress' ? (
            // PROGRESS STEP
            <div className="woo-step woo-step-progress">
              <div className="woo-progress-header">
                <h3>Importing {importJob?.importType?.toLowerCase()}...</h3>
                <p>Please don't close this window</p>
              </div>

              <div className="woo-progress-bar-container">
                <div
                  className="woo-progress-bar"
                  style={{ width: `${importProgress?.progress || 0}%` }}
                ></div>
              </div>

              <div className="woo-progress-stats">
                <div className="woo-progress-stat">
                  <span className="woo-stat-value">{importProgress?.processedItems || 0}</span>
                  <span className="woo-stat-label">Processed</span>
                </div>
                <div className="woo-progress-stat">
                  <span className="woo-stat-value">{importProgress?.totalItems || 0}</span>
                  <span className="woo-stat-label">Total</span>
                </div>
                <div className="woo-progress-stat woo-stat-success">
                  <span className="woo-stat-value">{importProgress?.successCount || 0}</span>
                  <span className="woo-stat-label">Success</span>
                </div>
                <div className="woo-progress-stat woo-stat-skipped">
                  <span className="woo-stat-value">{importProgress?.skippedCount || 0}</span>
                  <span className="woo-stat-label">Skipped</span>
                </div>
                <div className="woo-progress-stat woo-stat-failed">
                  <span className="woo-stat-value">{importProgress?.failedCount || 0}</span>
                  <span className="woo-stat-label">Failed</span>
                </div>
              </div>

              <button
                className="woo-btn woo-btn-outline"
                onClick={handleCancelImport}
              >
                Cancel Import
              </button>
            </div>
          ) : currentStep === 'complete' ? (
            // COMPLETE STEP
            <div className="woo-step woo-step-complete">
              <div className="woo-complete-icon">
                <Icons.Check />
              </div>
              <h3>Import Complete!</h3>

              <div className="woo-complete-stats">
                <div className="woo-complete-stat">
                  <span className="woo-stat-value">{importProgress?.successCount || 0}</span>
                  <span className="woo-stat-label">Imported Successfully</span>
                </div>
                <div className="woo-complete-stat">
                  <span className="woo-stat-value">{importProgress?.skippedCount || 0}</span>
                  <span className="woo-stat-label">Skipped (Duplicates)</span>
                </div>
                {importProgress?.failedCount > 0 && (
                  <div className="woo-complete-stat woo-stat-failed">
                    <span className="woo-stat-value">{importProgress.failedCount}</span>
                    <span className="woo-stat-label">Failed</span>
                  </div>
                )}
              </div>

              {/* Error Log */}
              {importProgress?.errors?.length > 0 && (
                <div className="woo-error-log">
                  <h4>Error Log ({importProgress.errors.length} errors)</h4>
                  <div className="woo-error-list">
                    {importProgress.errors.slice(0, 10).map((error, idx) => (
                      <div key={idx} className="woo-error-item">
                        <span className="woo-error-type">{error.type}</span>
                        <span className="woo-error-name">{error.wooName}</span>
                        <span className="woo-error-message">{error.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="woo-complete-actions">
                <button className="woo-btn woo-btn-primary" onClick={handleImportMore}>
                  Import More
                </button>
                <button className="woo-btn woo-btn-outline" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default WooCommerceIntegration;
